import { api, ApiError } from '../api';
import { statusBadge } from '../components/badge';
import { el, errorBanner, skeletonTable , appendAll} from '../components/layout';
import { parseProgress } from '../components/progress';
import { dataTable } from '../components/table';

type RunRow = {
  id: number;
  client: string;
  market: string;
  status: string;
  target_count: number;
  progress: string | null;
  created_at: string;
};

let pollTimer: number | null = null;

function stopPoll() {
  if (pollTimer != null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}

export function renderRunsPage(root: HTMLElement) {
  stopPoll();
  const content = el('div');
  appendAll(content, el('div', { className: 'page-header' }, [
      el('h1', { text: 'Runs' }),
      el('a', { href: '#/runs/new', className: 'btn btn-primary', text: 'New Run' }),
    ]),
  );

  const toolbar = el('div', { className: 'toolbar' });
  const statusSelect = el('select', { className: 'select', style: 'width:auto;min-width:160px' }, [
    el('option', { value: 'all', text: 'All statuses' }),
    ...['queued', 'running', 'needs_review', 'approved', 'exported', 'error'].map((s) =>
      el('option', { value: s, text: s }),
    ),
  ]) as HTMLSelectElement;
  const search = el('input', {
    className: 'input',
    style: 'max-width:240px',
    placeholder: 'Filter by client',
  }) as HTMLInputElement;
  appendAll(toolbar, statusSelect, search);
  content.appendChild(toolbar);

  const listHost = el('div');
  listHost.appendChild(skeletonTable());
  content.appendChild(listHost);
  root.appendChild(content);

  let items: RunRow[] = [];

  function scoredOf(run: RunRow): number {
    return parseProgress(run.progress).scored;
  }

  function paint() {
    const status = statusSelect.value;
    const q = search.value.trim().toLowerCase();
    const filtered = items.filter((r) => {
      if (status !== 'all' && r.status !== status) return false;
      if (q && !String(r.client || '').toLowerCase().includes(q)) return false;
      return true;
    });

    const table = dataTable<RunRow>({
      columns: [
        {
          key: 'id',
          label: 'ID',
          render: (r) => {
            const a = el('a', { href: `#/runs/${r.id}`, text: String(r.id) });
            return a;
          },
        },
        { key: 'client', label: 'Client', render: (r) => r.client || '-' },
        { key: 'market', label: 'Market', render: (r) => r.market || '-' },
        { key: 'status', label: 'Status', render: (r) => statusBadge(r.status) },
        { key: 'target', label: 'Target', render: (r) => String(r.target_count ?? '-') },
        { key: 'scored', label: 'Scored', render: (r) => String(scoredOf(r)) },
        {
          key: 'created',
          label: 'Created',
          render: (r) => String(r.created_at || '').replace('T', ' ').slice(0, 19),
        },
      ],
      rows: filtered,
    });
    listHost.replaceChildren(table);
  }

  async function load() {
    try {
      const data = await api<{ items: RunRow[] }>('/api/runs');
      items = data.items || [];
      paint();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      listHost.replaceChildren(errorBanner(msg, load));
    }
  }

  statusSelect.addEventListener('change', paint);
  search.addEventListener('input', paint);

  async function pollRunning() {
    const running = items.filter((r) => r.status === 'queued' || r.status === 'running');
    if (!running.length) return;
    for (const run of running) {
      try {
        const data = await api<{ run: RunRow }>(`/api/runs/${run.id}`);
        const idx = items.findIndex((r) => r.id === run.id);
        if (idx >= 0 && data.run) items[idx] = data.run as RunRow;
      } catch {
        /* keep prior */
      }
    }
    paint();
  }

  load().then(() => {
    stopPoll();
    pollTimer = window.setInterval(pollRunning, 4000);
  });
}

export function disposeRunsPage() {
  stopPoll();
}
